import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { DisclosureToggle } from '@/components/Disclosure';
import { StatusPill } from '@/components/Status';
import { WorkEmpty, WorkLoading, WorkNotice, WorkSegmented } from '@/features/orders/workLib';
import { TodayItemCard } from '@/features/today/TodayItemCard';
import { RUN_STATE_LABEL, groupToday, isLate, todayOrderAnchor, type TodayGroup } from '@/features/today/todayLib';
import { isOpen } from '@/features/runs/runState';
import { useOrdersDataStore } from '@/stores/ordersDataStore';
import { useTodayStore } from '@/stores/todayStore';
import type { TodaySource } from '@/api/today';

type Filter = 'all' | TodaySource;
const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'order', label: 'Orders' },
  { value: 'ask', label: 'Decisions' },
  { value: 'setup_submit', label: 'Submits' },
  { value: 'discrepancies', label: 'Counts' },
];

/**
 * Today (automation plan §1, W6): every item assigned to Matt, from every
 * home, ranked late / due within 3 days → money → oldest by the server. One
 * item per journey — an order carries its journey's other open asks under
 * "N more on this order" — with the facts that decide it showing (Matt's
 * choices from the 2026-10-29 mockup). Answered items leave the list and wait
 * under Sent to Claude with their run's state.
 */
export function TodayPage() {
  const { today, error, load, lateOpen, setLateOpen } = useTodayStore();
  const loadOverview = useOrdersDataStore((s) => s.load);
  const [filter, setFilter] = useState<Filter>('all');
  useEffect(() => { if (!today) void load(); }, [today, load]);

  // `#order-<journey id>` (To order's link): show everything and scroll to that card.
  const anchor = useLocation().hash.slice(1);
  useEffect(() => { if (anchor) { setFilter('all'); setLateOpen(true); } }, [anchor, setLateOpen]);
  const scrolledTo = useRef<string | null>(null);
  useEffect(() => {
    if (!anchor || !today || scrolledTo.current === anchor) return;
    scrolledTo.current = anchor;
    document.getElementById(anchor)?.scrollIntoView({ block: 'start' });
  }, [anchor, today]);

  // A tap changes asks, journeys and counts: reload Today and the shell's overview.
  const onChanged = () => { void load(); void loadOverview(); };

  const groups = useMemo(() => groupToday(today), [today]);
  const shown = filter === 'all' ? groups : groups.filter((g) => g.item.source === filter);
  const late = groups.filter((g) => isLate(g.item)).length;
  // The server ranks late first; the Late section folds away so due-soon is one glance down.
  const shownLate = shown.filter((g) => isLate(g.item));
  const shownRest = shown.filter((g) => !isLate(g.item));
  const withClaude = (today?.items ?? []).filter((it) => isOpen(it.session ?? null)).length;
  const soon = groups.filter((g) => g.item.why.some((c) => c.kind === 'due' && (c.days ?? 99) >= 0 && (c.days ?? 99) <= 3)).length;
  const filterCount = (f: Filter) => (f === 'all' ? groups.length : groups.filter((g) => g.item.source === f).length);

  const card = (g: TodayGroup) => (
    <TodayItemCard key={g.item.key} item={g.item} related={g.related} onChanged={onChanged}
      highlighted={Boolean(anchor && g.item.journey && anchor === todayOrderAnchor(g.item.journey.id) && g.item.source === 'order')} />
  );

  return (
    <div className="relative h-full overflow-y-auto bg-background">
      <PageHeader
        title="Today"
        about="Everything waiting on you, late and due-soon first, then the most money at stake. A button does the thing; anything Claude has to do waits under Sent to Claude until its run finishes."
        actions={today && (
          <div className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground tabular-nums">
            <span><b className="font-semibold text-foreground">{groups.length}</b> for you</span>
            {late > 0 && <StatusPill tone="danger">{late} late</StatusPill>}
            {soon > 0 && <StatusPill tone="warning">{soon} due in 3 days</StatusPill>}
            {withClaude > 0 && <StatusPill tone="info">{withClaude} with Claude</StatusPill>}
          </div>
        )}
      />
      <div className="mx-8 mb-8 flex max-w-[980px] flex-col gap-3 max-md:mx-3">
        {error && <WorkNotice tone="danger" onRetry={() => void load()}>{error}</WorkNotice>}
        {!today && !error && <WorkLoading />}
        {today && (
          <>
            {today.problems.length > 0 && (
              <WorkNotice tone="warning">{today.problems.length} item(s) could not be built: {today.problems.join('; ')}</WorkNotice>
            )}
            <WorkSegmented
              label="Show"
              value={filter}
              onChange={setFilter}
              options={FILTERS.filter((f) => f.value === 'all' || filterCount(f.value) > 0)
                .map((f) => ({ value: f.value, label: <>{f.label} <span className="tabular-nums opacity-70">{filterCount(f.value)}</span></> }))}
            />
            {shown.length === 0 && <WorkEmpty>Nothing waiting on you.</WorkEmpty>}
            {shownLate.length > 0 && (
              <DisclosureToggle open={lateOpen} onToggle={() => setLateOpen(!lateOpen)}
                className="self-start text-sm font-semibold text-foreground">
                Late <span className="tabular-nums text-muted-foreground">{shownLate.length}</span>
              </DisclosureToggle>
            )}
            {lateOpen && shownLate.map(card)}
            {shownLate.length > 0 && shownRest.length > 0 && (
              <h2 className="mt-1 text-sm font-semibold">
                Not late <span className="tabular-nums font-normal text-muted-foreground">{shownRest.length}</span>
              </h2>
            )}
            {shownRest.map(card)}

            {today.queued.length > 0 && (
              <section className="mt-4 grid gap-1.5" aria-labelledby="today-queued">
                <h2 id="today-queued" className="font-display text-[17px] font-bold">Sent to Claude</h2>
                <ul className="grid gap-1 text-sm">
                  {today.queued.map((q) => (
                    <li key={q.ask_id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 rounded-md bg-card px-3 py-2 shadow-row">
                      <span className="min-w-0 flex-[1_1_20rem]">{q.title}</span>
                      <StatusPill tone={q.run.state === 'finished' ? 'success' : 'info'}>{RUN_STATE_LABEL[q.run.state] ?? q.run.state}</StatusPill>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {today.elsewhere.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
                <span>Also waiting:</span>
                {today.elsewhere.map((e) => (
                  <Link key={e.queue} to={e.href} className="text-foreground hover:underline">
                    {e.label} <span className="tabular-nums text-muted-foreground">{e.count.toLocaleString()}</span>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
